"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { memo, useState, useRef, useCallback, useEffect, useMemo } from "react";
import { debounce, Box } from "@mui/material";
import { Skeleton } from "../../Feedback/Skeleton/index.js";
import { SkeletonVariant } from "../../Feedback/Skeleton/enums.js";
import { LowerControls } from "./LowerControls/index.js";
import { NavigationButton } from "./NavigationButton/index.js";
import { FOCUSABLE_ELEMENT_SELECTOR, safelyUnmountNodeAttr, executeForFocusDescendants, safelyMountNodeAttr, executeForFirstFocusDescendant } from "./Util/AccessibilityHelper.js";
import { DEFAULT_DELAY_MS, DEFAULT_MAX_ITEMS_PER_SLIDE, AUTOPLAY_BUTTON_PAUSE_LABEL, AUTOPLAY_BUTTON_PLAY_LABEL, COMPONENT_NAME } from "./constants.js";
import { useResponsiveItemsPerSlide } from "./hooks.js";
import { CarouselSlide, BaseCarousel, CarouselContainer, scrollbarStyles, scrollbarInnerStyles } from "./styles.js";
import { shuffleArray } from "./utils.js";
import { getDataTestId } from "../../Util/index.js";
import useCheckScreen from "../../../hooks/useCheckScreen/index.js";
const TOGGLE_FOCUSABLE_SLIDE_NODES_DEBOUNCE_MS = 500;
const DefaultSkeletonComponent = () => {
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: "100%"
      },
      children: /* @__PURE__ */ jsx(
        Box,
        {
          sx: {
            minWidth: `100%`
          },
          children: /* @__PURE__ */ jsx(
            Skeleton,
            {
              height: "35vh",
              variant: SkeletonVariant.Rectangular
            }
          )
        }
      )
    }
  );
};
const Carousel = memo((props) => {
  const {
    containerSx = {},
    contentTypeLabel = "Promo",
    contentTypeJoinder = "of",
    delayInMs = DEFAULT_DELAY_MS,
    disableControls = false,
    disableBottomControls = false,
    isAutoplay = false,
    items: startingItems = [],
    itemsPerSlide = DEFAULT_MAX_ITEMS_PER_SLIDE,
    label = "Featured promos and announcements slideshow",
    labelledBy,
    navigationButtonBottomOffset = "auto",
    navigationButtonTopOffset = "auto",
    navigationButtonsBottomAlign = false,
    navigationButtonsMobileDisplay = false,
    nextLabel = "Next promo",
    pauseLabel = AUTOPLAY_BUTTON_PAUSE_LABEL,
    playLabel = AUTOPLAY_BUTTON_PLAY_LABEL,
    previousLabel = "Previous promo",
    slideContainerSx = {},
    carouselContainerSx = {},
    slideGap = "",
    uniqueId = "",
    wrapAround = true,
    peekAdjacent = "",
    dragFree = false,
    isScrollbar = false,
    skeletonComponent,
    randomizeItems
  } = props;
  const [randomizedItems, setRandomizedItems] = useState([]);
  const items = randomizeItems && (randomizedItems == null ? void 0 : randomizedItems.length) > 0 ? randomizedItems : startingItems;
  const itemsLength = items.length;
  const emblaOptions = {
    align: peekAdjacent ? "center" : "start",
    duration: 20,
    // inViewThreashold is the amount of slide item that is visible in the carousel slide.
    inViewThreshold: 0.8,
    slidesToScroll: "auto",
    loop: wrapAround,
    watchFocus: false,
    dragFree
  };
  const carousel = useEmblaCarousel(
    emblaOptions,
    isAutoplay ? [
      Autoplay({
        delay: delayInMs,
        playOnInit: isAutoplay,
        stopOnFocusIn: true,
        stopOnInteraction: true
      })
    ] : []
  );
  const [currItemsPerSlide] = useResponsiveItemsPerSlide(itemsPerSlide);
  const carouselContainerRef = useRef(null);
  const [carouselRef, carouselApi] = carousel;
  const itemRefs = useRef([]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const leadingFirstItemRef = useRef(null);
  const leadingFirstVisibleItemInWrapAroundRef = useRef(null);
  const trailingLastItemRef = useRef(null);
  const trailingRemainderItemRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(isAutoplay);
  const { isMobile } = useCheckScreen();
  const scrollbarRef = useRef(null);
  const [scrollbarPosition, setScrollbarPosition] = useState(0);
  const innerScrollWidth = 20;
  const toggleIsActive = useCallback(() => {
    setIsActive((prev) => !prev);
  }, []);
  const itemRemainder = items.length % currItemsPerSlide;
  const handleKeyDown = useCallback(
    (event) => {
      var _a, _b, _c, _d, _e, _f;
      if (wrapAround && items.length > currItemsPerSlide && itemRemainder > 0 && event.key === "Tab") {
        const activeElement = document.activeElement;
        const lastSlideIndex = itemRefs.current.length - 1;
        const slidesInView = carouselApi == null ? void 0 : carouselApi.slidesInView();
        if ((slidesInView == null ? void 0 : slidesInView.includes(0)) && (slidesInView == null ? void 0 : slidesInView.includes(lastSlideIndex))) {
          const isNavigatingForward = !event.shiftKey;
          const previousButton = prevButtonRef == null ? void 0 : prevButtonRef.current;
          const nextButton = nextButtonRef == null ? void 0 : nextButtonRef.current;
          const {
            remainderSectionFocusableElements,
            startingSectionFocusableElements
          } = slidesInView.sort((a, b) => a - b).reduce(
            (focusableElementSections, slideIndex) => {
              var _a2;
              const focusableElements = ((_a2 = itemRefs.current[slideIndex]) == null ? void 0 : _a2.querySelectorAll(
                FOCUSABLE_ELEMENT_SELECTOR
              )) || [];
              if (focusableElements.length > 0) {
                if (slideIndex <= currItemsPerSlide - itemRemainder) {
                  focusableElementSections.startingSectionFocusableElements = [
                    ...focusableElementSections.startingSectionFocusableElements,
                    ...focusableElements
                  ];
                } else {
                  focusableElementSections.remainderSectionFocusableElements = [
                    ...focusableElementSections.remainderSectionFocusableElements,
                    ...focusableElements
                  ];
                }
              }
              return focusableElementSections;
            },
            {
              remainderSectionFocusableElements: [],
              startingSectionFocusableElements: []
            }
          );
          const firstFocusableRemainderElement = remainderSectionFocusableElements[0];
          const lastFocusableRemainderElement = remainderSectionFocusableElements[remainderSectionFocusableElements.length - 1];
          const firstFocusableStartingSectionElement = startingSectionFocusableElements[0];
          const lastFocusableStartingSectionElement = startingSectionFocusableElements[startingSectionFocusableElements.length - 1];
          if (isNavigatingForward) {
            if (activeElement === previousButton) {
              (_a = leadingFirstVisibleItemInWrapAroundRef == null ? void 0 : leadingFirstVisibleItemInWrapAroundRef.current) == null ? void 0 : _a.focus();
            } else if (lastFocusableRemainderElement === activeElement) {
              (_b = leadingFirstItemRef == null ? void 0 : leadingFirstItemRef.current) == null ? void 0 : _b.focus();
            } else {
              if (lastFocusableStartingSectionElement === activeElement) {
                if (nextButton) {
                  event.preventDefault();
                  nextButton == null ? void 0 : nextButton.focus();
                } else {
                  (_c = trailingLastItemRef == null ? void 0 : trailingLastItemRef.current) == null ? void 0 : _c.focus();
                }
              }
            }
          } else {
            if (activeElement === nextButton) {
              (_d = trailingRemainderItemRef == null ? void 0 : trailingRemainderItemRef.current) == null ? void 0 : _d.focus();
            } else if (firstFocusableStartingSectionElement === activeElement) {
              (_e = trailingLastItemRef == null ? void 0 : trailingLastItemRef.current) == null ? void 0 : _e.focus();
            } else if (firstFocusableRemainderElement === activeElement) {
              if (previousButton) {
                event.preventDefault();
                previousButton.focus();
              } else {
                (_f = leadingFirstItemRef == null ? void 0 : leadingFirstItemRef.current) == null ? void 0 : _f.focus();
              }
            }
          }
        }
      }
    },
    [
      carouselApi,
      wrapAround,
      itemRemainder,
      items.length,
      currItemsPerSlide
    ]
  );
  const getUpdatedScrollbarPosition = useCallback(
    (index) => {
      if (!carouselApi) {
        return;
      }
      return carouselApi.scrollSnapList()[index] * (100 - innerScrollWidth);
    },
    [carouselApi]
  );
  useEffect(() => {
    if (randomizeItems) {
      setRandomizedItems(shuffleArray(startingItems));
    }
  }, []);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
  useEffect(() => {
    if (!carouselApi || !isAutoplay) return;
    const autoplay = carouselApi.plugins().autoplay;
    const mediaQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    if (autoplay && mediaQuery.matches) {
      autoplay.stop();
      setIsActive(false);
    }
  }, [carouselApi, isAutoplay]);
  const onSelect = useCallback(() => {
    if (!carouselApi) return;
    if (isScrollbar) {
      const index = carouselApi.selectedScrollSnap();
      const updatedScrollbarPosition = getUpdatedScrollbarPosition(index);
      setScrollbarPosition(updatedScrollbarPosition || 0);
    }
    setSelectedIndex(carouselApi.selectedScrollSnap());
    setPrevBtnEnabled(carouselApi.canScrollPrev());
    setNextBtnEnabled(carouselApi.canScrollNext());
  }, [carouselApi, getUpdatedScrollbarPosition, isScrollbar]);
  const onResize = useCallback(() => {
    if (!carouselApi) return;
    setScrollSnaps(carouselApi.scrollSnapList());
    setPrevBtnEnabled(carouselApi.canScrollPrev());
    setNextBtnEnabled(carouselApi.canScrollNext());
  }, [carouselApi, setScrollSnaps]);
  useEffect(() => {
    if (!carouselApi) return;
    onSelect();
    onResize();
    carouselApi.on("select", onSelect);
    carouselApi.on("resize", onResize);
  }, [carouselApi, itemsLength, onResize, onSelect]);
  const getSlideElementsByVisibility = useCallback(() => {
    const visible = [];
    const invisible = [];
    if (carouselApi) {
      const slides = carouselApi.slideNodes();
      for (let index = 0; index <= slides.length; index++) {
        const slide = slides[index];
        if (slide !== void 0) {
          carouselApi.slidesInView().includes(index) ? visible.push(slide) : invisible.push(slide);
        }
      }
    }
    return [visible, invisible];
  }, [carouselApi]);
  const toggleFocusableSlideNodes = useCallback(() => {
    if (!carouselApi) return;
    const [visible, invisible] = getSlideElementsByVisibility();
    for (const slide of visible) {
      safelyUnmountNodeAttr(slide, "tabindex");
      safelyUnmountNodeAttr(slide, "aria-hidden");
      executeForFocusDescendants(slide, (child) => {
        safelyUnmountNodeAttr(child, "tabindex");
        safelyUnmountNodeAttr(child, "aria-hidden");
      });
    }
    for (const slide of invisible) {
      safelyMountNodeAttr(slide, "tabindex", "-1");
      safelyMountNodeAttr(slide, "aria-hidden", "true");
      executeForFocusDescendants(slide, (child) => {
        safelyMountNodeAttr(child, "tabindex", "-1");
        safelyMountNodeAttr(child, "aria-hidden", "true");
      });
    }
  }, [carouselApi, getSlideElementsByVisibility]);
  const updateFocus = useCallback(() => {
    if (!carouselApi) return;
    if (isActive) return;
    const carouselContainerElement = carouselContainerRef == null ? void 0 : carouselContainerRef.current;
    if (wrapAround && (!carouselContainerElement || !document.activeElement || !carouselContainerElement.contains(document.activeElement))) {
      return;
    }
    const [visible] = getSlideElementsByVisibility();
    const visibleSlideCount = visible.length;
    const getNextFocusIndex = () => {
      const slideCount = carouselApi.slideNodes().length;
      const slidesInView = carouselApi.slidesInView();
      const remainder = slideCount % visibleSlideCount;
      if (carouselApi.canScrollNext() && !(slidesInView.includes(0) && slidesInView.includes(slideCount - 1))) {
        return 0;
      }
      return remainder === 0 ? 0 : visibleSlideCount - remainder;
    };
    if (visibleSlideCount > 0) {
      executeForFirstFocusDescendant(
        visible[getNextFocusIndex()],
        (child) => {
          child.focus({ focusVisible: true });
        }
      );
    }
  }, [carouselApi, isActive, wrapAround, getSlideElementsByVisibility]);
  useEffect(() => {
    if (!carouselApi) return;
    toggleFocusableSlideNodes();
    carouselApi.on("slidesInView", toggleFocusableSlideNodes);
  }, [carouselApi, toggleFocusableSlideNodes]);
  const debounceToggleFocusableSlideNodes = useMemo(
    () => debounce(
      () => toggleFocusableSlideNodes(),
      TOGGLE_FOCUSABLE_SLIDE_NODES_DEBOUNCE_MS
    ),
    [toggleFocusableSlideNodes]
  );
  useEffect(() => {
    if (!carouselApi || !(carouselContainerRef == null ? void 0 : carouselContainerRef.current)) return;
    const observer = new MutationObserver(() => {
      debounceToggleFocusableSlideNodes();
    });
    observer.observe(carouselContainerRef.current, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [carouselApi, debounceToggleFocusableSlideNodes]);
  useEffect(() => {
    if (!carouselApi) return;
    if (isActive) {
      carouselApi.off("settle", updateFocus);
    } else {
      carouselApi.on("settle", updateFocus);
    }
  }, [carouselApi, isActive, updateFocus]);
  const onButtonAutoplayClick = useCallback(
    (callback) => {
      var _a;
      const autoplay = (_a = carouselApi == null ? void 0 : carouselApi.plugins()) == null ? void 0 : _a.autoplay;
      if (autoplay) {
        const resetOrStop = autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop;
        resetOrStop();
        setIsActive(false);
      }
      callback();
    },
    [carouselApi]
  );
  const onMouseEnter = useCallback(() => {
    var _a;
    const autoplay = (_a = carouselApi == null ? void 0 : carouselApi.plugins()) == null ? void 0 : _a.autoplay;
    if (isAutoplay && (autoplay == null ? void 0 : autoplay.isPlaying())) {
      setIsHovered(true);
      autoplay.stop();
    }
  }, [carouselApi, isAutoplay]);
  const onMouseLeave = useCallback(() => {
    var _a;
    const autoplay = (_a = carouselApi == null ? void 0 : carouselApi.plugins()) == null ? void 0 : _a.autoplay;
    if (isAutoplay && autoplay && !(autoplay == null ? void 0 : autoplay.isPlaying())) {
      setIsHovered(false);
      isActive && !isFocused && autoplay.play();
    }
  }, [carouselApi, isActive, isAutoplay, isFocused]);
  const onButtonFocus = useCallback(() => {
    var _a;
    const autoplay = (_a = carouselApi == null ? void 0 : carouselApi.plugins()) == null ? void 0 : _a.autoplay;
    if (isAutoplay && (autoplay == null ? void 0 : autoplay.isPlaying())) {
      setIsFocused(true);
      autoplay.stop();
    }
  }, [carouselApi, isAutoplay]);
  const onButtonBlur = useCallback(() => {
    var _a;
    const autoplay = (_a = carouselApi == null ? void 0 : carouselApi.plugins()) == null ? void 0 : _a.autoplay;
    if (isAutoplay && autoplay && !(autoplay == null ? void 0 : autoplay.isPlaying())) {
      setIsFocused(false);
      isActive && !isHovered && autoplay.play();
    }
  }, [carouselApi, isActive, isAutoplay, isHovered]);
  const renderedItems = useMemo(() => {
    const lastItemIndex = items.length - 1;
    const firstVisibleItemInWrapAround = items.length - itemRemainder;
    return items.map((item, i) => {
      let leadingAnchorLinkRef;
      let trailingAnchorLinkRef;
      if (i === firstVisibleItemInWrapAround) {
        leadingAnchorLinkRef = leadingFirstVisibleItemInWrapAroundRef;
      }
      if (i === lastItemIndex) {
        trailingAnchorLinkRef = trailingLastItemRef;
      }
      if (i === 0) {
        leadingAnchorLinkRef = leadingFirstItemRef;
      }
      if (i === currItemsPerSlide - itemRemainder) {
        trailingAnchorLinkRef = trailingRemainderItemRef;
      }
      return /* @__PURE__ */ jsxs(
        CarouselSlide,
        {
          peekAdjacent,
          itemsPerSlide: currItemsPerSlide,
          slideGap,
          ref: (el) => itemRefs.current[i] = el,
          sx: {
            ...slideContainerSx
          },
          children: [
            leadingAnchorLinkRef && /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  position: "absolute",
                  height: 0,
                  width: 0,
                  top: 0,
                  zIndex: -100
                },
                ref: leadingAnchorLinkRef,
                tabIndex: -1,
                "aria-hidden": true
              }
            ),
            item,
            trailingAnchorLinkRef && /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  position: "absolute",
                  height: 0,
                  width: 0,
                  top: 0,
                  zIndex: -100
                },
                ref: trailingAnchorLinkRef,
                tabIndex: -1,
                "aria-hidden": true
              }
            )
          ]
        },
        `carousel_slide_${i}`
      );
    });
  }, [
    items,
    currItemsPerSlide,
    peekAdjacent,
    slideGap,
    slideContainerSx,
    itemRemainder
  ]);
  const navigationButtonCommonProps = useMemo(
    () => ({
      carousel,
      onButtonAutoplayClick,
      onButtonBlur,
      onButtonFocus
    }),
    [carousel, onButtonAutoplayClick, onButtonBlur, onButtonFocus]
  );
  const previousButtonBaseProps = useMemo(
    () => ({
      ...navigationButtonCommonProps,
      ariaLabel: previousLabel,
      direction: "previous"
    }),
    [navigationButtonCommonProps, previousLabel]
  );
  const nextButtonBaseProps = useMemo(
    () => ({
      ...navigationButtonCommonProps,
      ariaLabel: nextLabel,
      direction: "next"
    }),
    [navigationButtonCommonProps, nextLabel]
  );
  const isPreviousButtonVisible = useMemo(
    () => !disableControls && (!isMobile || navigationButtonsMobileDisplay) && prevBtnEnabled,
    [
      disableControls,
      isMobile,
      navigationButtonsMobileDisplay,
      prevBtnEnabled
    ]
  );
  const isNextButtonVisible = useMemo(
    () => !disableControls && (!isMobile || navigationButtonsMobileDisplay) && nextBtnEnabled,
    [
      disableControls,
      isMobile,
      navigationButtonsMobileDisplay,
      nextBtnEnabled
    ]
  );
  let SkeletonComponent = skeletonComponent || DefaultSkeletonComponent;
  if (!SkeletonComponent) {
    SkeletonComponent = DefaultSkeletonComponent;
  }
  if (randomizeItems && (randomizedItems == null ? void 0 : randomizedItems.length) === 0) {
    return /* @__PURE__ */ jsx(
      Carousel,
      {
        ...props,
        randomizeItems: false,
        items: startingItems.map((_, index) => {
          return /* @__PURE__ */ jsx(Box, { sx: { width: "100%" }, children: /* @__PURE__ */ jsx(SkeletonComponent, {}) }, index);
        })
      }
    );
  }
  const handleScrollbarDrag = (e) => {
    if (!scrollbarRef.current || !carouselApi) return;
    const scrollbar = scrollbarRef.current;
    const rect = scrollbar.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const totalSlides = Math.ceil(items.length / currItemsPerSlide);
    const slideWidth = scrollbar.clientWidth / totalSlides;
    const newIndex = Math.min(
      Math.max(0, Math.floor(offsetX / slideWidth)),
      totalSlides - 1
    );
    carouselApi.scrollTo(newIndex);
    const updatedScrollbarPosition = getUpdatedScrollbarPosition(newIndex);
    setScrollbarPosition(updatedScrollbarPosition || 0);
  };
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleScrollbarDrag(e);
    const handleMouseMove = (moveEvent) => handleScrollbarDrag(moveEvent);
    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };
  return items.length ? /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        width: "100%",
        position: "relative",
        ...containerSx
      },
      "aria-label": labelledBy ? void 0 : label,
      "aria-labelledby": labelledBy,
      "aria-roledescription": "carousel",
      "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
      onMouseEnter,
      onMouseLeave,
      role: "region",
      ref: carouselContainerRef,
      children: [
        /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              width: "100%",
              display: "flex",
              alignItems: "center"
            },
            children: [
              /* @__PURE__ */ jsx(
                NavigationButton,
                {
                  ...previousButtonBaseProps,
                  innerRef: prevButtonRef,
                  isVisible: isPreviousButtonVisible && !navigationButtonsBottomAlign,
                  navigationButtonBottomOffset,
                  navigationButtonTopOffset,
                  isScrollbar
                }
              ),
              /* @__PURE__ */ jsx(BaseCarousel, { ref: carouselRef, children: /* @__PURE__ */ jsx(
                CarouselContainer,
                {
                  slideGap,
                  sx: carouselContainerSx,
                  children: renderedItems
                }
              ) }),
              /* @__PURE__ */ jsx(
                NavigationButton,
                {
                  ...nextButtonBaseProps,
                  innerRef: nextButtonRef,
                  isVisible: isNextButtonVisible && !navigationButtonsBottomAlign,
                  navigationButtonBottomOffset,
                  navigationButtonTopOffset,
                  isScrollbar
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          LowerControls,
          {
            carousel,
            contentTypeJoinder,
            contentTypeLabel,
            handleAutoplayClick: toggleIsActive,
            isActive,
            isAutoplay,
            isVisible: !disableControls && !disableBottomControls,
            nextButtonProps: {
              ...nextButtonBaseProps,
              isVisible: isNextButtonVisible && navigationButtonsBottomAlign
            },
            onButtonAutoplayClick,
            onButtonBlur,
            onButtonFocus,
            pauseLabel,
            playLabel,
            previousButtonProps: {
              ...previousButtonBaseProps,
              isVisible: isPreviousButtonVisible && navigationButtonsBottomAlign
            },
            scrollSnaps,
            selectedIndex
          }
        ),
        isScrollbar && /* @__PURE__ */ jsx(
          Box,
          {
            sx: scrollbarStyles,
            "data-testid": "scrollbar",
            onMouseDown: handleMouseDown,
            ref: scrollbarRef,
            children: /* @__PURE__ */ jsx(
              Box,
              {
                "data-testid": "scrollbar-inner",
                sx: {
                  ...scrollbarInnerStyles,
                  width: `${innerScrollWidth}%`,
                  left: `${scrollbarPosition}%`
                }
              }
            )
          }
        )
      ]
    }
  ) : null;
});
Carousel.displayName = COMPONENT_NAME;
export {
  Carousel
};
